"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CountryList from "country-list-with-dial-code-and-flag";
import { Input, Select, Typography } from "@/components/ui";
import { NewButton } from "@/components/ui/NewButton";
import { CalendlyWidget } from "@/components/CalendlyWidget";
import { useGeoLocation } from "@/lib/hooks/useGeoLocation";
import { useUTMContext } from "@/lib/context/UTMProvider";
import { cn } from "@/lib/utils";
import {
  COMPANY_SIZE_OPTIONS,
  LOCATION_OPTIONS,
  PRIMARY_NEED_OPTIONS,
  SCREEN_OPTIONS,
  ZOHO_HARDWARE_QUOTE_FORM_ACTION,
  ZOHO_HARDWARE_QUOTE_IFRAME_TARGET,
} from "@/lib/data/hardware-quote-zoho-form";

const HARDWARE_CALENDLY_URL =
  "https://calendly.com/d/d3xs-6gt-64c/get-a-hardware-quote-talk-to-a-specialist";

type FormField =
  | "firstName"
  | "lastName"
  | "email"
  | "company"
  | "phone"
  | "companySize"
  | "locations"
  | "screens"
  | "primaryNeed";

type FormErrors = Partial<Record<FormField, string>>;

const fieldClassName = "rounded-lg border-[#D7D9DE] bg-white font-normal";

const selectFieldClassName = (value: string) =>
  cn(fieldClassName, "[&_option]:text-text-primary", value ? "text-text-primary" : "text-gray-400");

const emptyForm = {
  firstName: "",
  lastName: "",
  email: "",
  company: "",
  phone: "",
  companySize: "",
  locations: "",
  screens: "",
  primaryNeed: "",
};

// The conversion page URL has utm params stripped; keep the gclid on it so it reaches the lead.
const withGclid = (url: string | undefined, gclid: string) => {
  if (!url) return "";
  if (!gclid) return url;
  try {
    const parsed = new URL(url);
    if (!parsed.searchParams.has("gclid")) parsed.searchParams.set("gclid", gclid);
    return parsed.toString();
  } catch {
    return url;
  }
};

export default function HardwareQuoteForm() {
  const { geoData } = useGeoLocation();
  const { attributionData, getAllTrackingData } = useUTMContext();
  const formRef = useRef<HTMLFormElement>(null);
  const submittedRef = useRef(false);
  const timeoutRef = useRef<number | undefined>(undefined);
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [country, setCountry] = useState("us");
  const [selectedDialCode, setSelectedDialCode] = useState("+1");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [hasStarted, setHasStarted] = useState(false);
  const [calendlyUserData, setCalendlyUserData] = useState<{
    name: string;
    email: string;
    phone: string;
    company_name: string;
  } | null>(null);

  // Build UTM hidden-field values once, re-computed when tracking data changes
  const trackingData = getAllTrackingData();
  const utmFields = trackingData.utm || {};
  const gclidValue = attributionData?.click_ids?.gclid || "";

  const countryData = useMemo(() => {
    const countryMap = new Map<
      string,
      { code: string; name: string; flag: string; dialCodes: string[] }
    >();

    CountryList.getAll().forEach(entry => {
      const code = entry.code;
      if (!countryMap.has(code)) {
        countryMap.set(code, {
          code: entry.code,
          name: entry.name,
          flag: entry.flag,
          dialCodes: [],
        });
      }
      if (entry.dialCode) {
        countryMap.get(code)?.dialCodes.push(entry.dialCode);
      }
    });

    return Array.from(countryMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const getAvailableDialCodes = useCallback(
    (countryCode: string) => {
      const match = countryData.find(item => item.code.toLowerCase() === countryCode.toLowerCase());
      return match?.dialCodes?.length ? match.dialCodes : ["+1"];
    },
    [countryData]
  );

  const currentCountry = useMemo(
    () => countryData.find(item => item.code.toLowerCase() === country.toLowerCase()),
    [countryData, country]
  );

  useEffect(() => {
    if (!geoData?.countryCode) return;
    const detected = geoData.countryCode.toLowerCase();
    setCountry(detected);
    setSelectedDialCode(getAvailableDialCodes(detected)[0]);
  }, [geoData, getAvailableDialCodes]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, []);

  const markFormStart = () => {
    if (hasStarted) return;
    setHasStarted(true);
    window.dispatchEvent(
      new CustomEvent("mixpanelEvent", {
        detail: { type: "hardware-quote-form-start" },
      })
    );
  };

  const setField = (name: FormField, value: string) => {
    markFormStart();
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const fieldByZohoName: Record<string, FormField> = {
      Name_First: "firstName",
      Name_Last: "lastName",
      Email: "email",
      SingleLine: "company",
      PhoneNumber_countrycode: "phone",
      Dropdown: "companySize",
      Dropdown1: "locations",
      Dropdown2: "screens",
      Dropdown3: "primaryNeed",
    };
    const field = fieldByZohoName[e.target.name];
    if (!field) return;
    setField(field, e.target.value);
  };

  const validateForm = (): boolean => {
    const nextErrors: FormErrors = {};

    if (!formData.firstName.trim()) nextErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) nextErrors.lastName = "Last name is required";

    if (!formData.email.trim()) {
      nextErrors.email = "Work email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      nextErrors.email = "Please enter a valid email address";
    }

    if (!formData.company.trim()) nextErrors.company = "Company is required";

    const phoneDigits = formData.phone.replace(/\D/g, "");
    if (!phoneDigits) {
      nextErrors.phone = "Phone number is required";
    } else if (phoneDigits.length < 6) {
      nextErrors.phone = "Please enter a valid phone number";
    }

    if (!formData.companySize) nextErrors.companySize = "Company size is required";
    if (!formData.locations) nextErrors.locations = "Number of locations is required";
    if (!formData.screens) nextErrors.screens = "Number of screens is required";
    if (!formData.primaryNeed) nextErrors.primaryNeed = "Primary need is required";

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const trackSubmit = () => {
    window.dispatchEvent(
      new CustomEvent("mixpanelEvent", {
        detail: {
          type: "hardware-quote-form-submit",
          data: {
            email: formData.email,
            name: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
            company_name: formData.company,
            phone: `${selectedDialCode}${formData.phone.replace(/\D/g, "")}`,
            company_size: formData.companySize,
            number_of_locations: formData.locations,
            number_of_screens: formData.screens,
            primary_need: formData.primaryNeed,
          },
        },
      })
    );
  };

  const handleIframeLoad = () => {
    if (!submittedRef.current) return;
    submittedRef.current = false;
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    setSubmitStatus("success");
    setIsSubmitting(false);

    // Open the hardware Calendly popup after a successful submit
    setCalendlyUserData({
      name: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
      email: formData.email,
      phone: `${selectedDialCode}${formData.phone.replace(/\D/g, "")}`,
      company_name: formData.company,
    });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm() || !formRef.current) return;

    trackSubmit();
    setIsSubmitting(true);
    setSubmitStatus("idle");
    submittedRef.current = true;

    if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(() => {
      if (!submittedRef.current) return;
      submittedRef.current = false;
      setIsSubmitting(false);
      setSubmitStatus("error");
    }, 15000);

    formRef.current.submit();
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setErrors({});
    setSubmitStatus("idle");
    setHasStarted(false);
    setIsSubmitting(false);
    const detected = geoData?.countryCode?.toLowerCase() || "us";
    setCountry(detected);
    setSelectedDialCode(getAvailableDialCodes(detected)[0]);
  };

  return (
    <div className="w-full">
      {submitStatus === "success" ? (
        <div className="rounded-xl border border-green-200 bg-green-50 p-6 md:p-8">
          <Typography as="h3" variant="h4" className="text-green-900">
            Request received
          </Typography>
          <Typography as="p" variant="body" className="mt-2 text-green-800">
            Thank you. A hardware specialist will contact you to scope your rollout.
          </Typography>
          <button
            type="button"
            onClick={resetForm}
            className="mt-5 text-sm font-semibold text-primary hover:text-primary-hover"
          >
            Submit another request →
          </button>
        </div>
      ) : null}

      {submitStatus !== "success" ? (
        <iframe
          name={ZOHO_HARDWARE_QUOTE_IFRAME_TARGET}
          title="Hardware quote form submission"
          hidden
          onLoad={handleIframeLoad}
        />
      ) : null}

      {submitStatus !== "success" ? (
        <form
          ref={formRef}
          data-lead="hardware-quote"
          action={ZOHO_HARDWARE_QUOTE_FORM_ACTION}
          method="POST"
          encType="multipart/form-data"
          acceptCharset="UTF-8"
          target={ZOHO_HARDWARE_QUOTE_IFRAME_TARGET}
          onSubmit={handleSubmit}
          className="grid grid-cols-1 gap-4 sm:grid-cols-2"
        >
          <input type="hidden" name="zf_referrer_name" value="" />
          <input type="hidden" name="zf_redirect_url" value="" />
          <input type="hidden" name="zc_gad" value={gclidValue} />
          <input type="hidden" name="PhoneNumber_countrycodeval" value={selectedDialCode} />
          {/* UTM / attribution hidden fields. Names match the contact-form Zoho lead fields,
              so the Zoho form's field link names must be set to these exact values. */}
          <input type="hidden" name="utm_source" value={utmFields.utm_source || ""} />
          <input type="hidden" name="utm_medium" value={utmFields.utm_medium || ""} />
          <input type="hidden" name="utm_campaign" value={utmFields.utm_campaign || ""} />
          <input type="hidden" name="utm_content" value={utmFields.utm_content || ""} />
          <input type="hidden" name="utm_term" value={utmFields.utm_term || ""} />
          <input
            type="hidden"
            name="utm_first_visited_page"
            value={utmFields.utm_first_visited_page || ""}
          />
          <input
            type="hidden"
            name="utm_conversion_page"
            value={withGclid(utmFields.utm_conversion_page, gclidValue)}
          />
          <input type="hidden" name="gclid" value={gclidValue} />

          <Input
            id="hardware-first-name"
            name="Name_First"
            type="text"
            label="First name*"
            placeholder="First name"
            size="md"
            maxLength={255}
            value={formData.firstName}
            onChange={handleChange}
            inputClassName={fieldClassName}
            error={errors.firstName}
            required
            autoComplete="given-name"
          />
          <Input
            id="hardware-last-name"
            name="Name_Last"
            type="text"
            label="Last name*"
            placeholder="Last name"
            size="md"
            maxLength={255}
            value={formData.lastName}
            onChange={handleChange}
            inputClassName={fieldClassName}
            error={errors.lastName}
            required
            autoComplete="family-name"
          />
          <Input
            id="hardware-email"
            name="Email"
            type="email"
            label="Work email*"
            placeholder="Work email"
            size="md"
            maxLength={255}
            value={formData.email}
            onChange={handleChange}
            inputClassName={fieldClassName}
            error={errors.email}
            required
            autoComplete="email"
          />
          <Input
            id="hardware-company"
            name="SingleLine"
            type="text"
            label="Company*"
            placeholder="Company"
            size="md"
            maxLength={255}
            value={formData.company}
            onChange={handleChange}
            inputClassName={fieldClassName}
            error={errors.company}
            required
            autoComplete="organization"
          />

          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <label
              id="hardware-phone-label"
              htmlFor="hardware-phone"
              className="text-sm font-medium text-text-primary"
            >
              Phone number*
            </label>
            <div className="flex w-full flex-row gap-2">
              <div className="relative">
                <div className="flex h-11 w-10 items-center justify-center rounded-lg border border-[#D7D9DE] bg-white text-base">
                  <span aria-hidden>{currentCountry?.flag || "🇺🇸"}</span>
                </div>
                <select
                  id="hardware-country"
                  aria-label="Country"
                  value={country}
                  onChange={e => {
                    markFormStart();
                    const next = e.target.value;
                    setCountry(next);
                    setSelectedDialCode(getAvailableDialCodes(next)[0]);
                  }}
                  className="absolute inset-0 h-11 w-14 cursor-pointer opacity-0"
                >
                  {countryData.map(item => (
                    <option key={item.code} value={item.code.toLowerCase()}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </div>
              <div
                className={cn(
                  "flex h-11 w-full items-center gap-2 rounded-lg border bg-white px-4",
                  errors.phone ? "border-red-500" : "border-[#D7D9DE]"
                )}
              >
                {currentCountry && currentCountry.dialCodes.length > 1 ? (
                  <select
                    value={selectedDialCode}
                    onChange={e => {
                      markFormStart();
                      setSelectedDialCode(e.target.value);
                    }}
                    className="cursor-pointer border-none bg-transparent text-sm font-medium text-text-primary outline-none md:text-base"
                    aria-label="Country code"
                  >
                    {currentCountry.dialCodes.map(dialCode => (
                      <option key={dialCode} value={dialCode}>
                        {dialCode}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="text-sm font-medium text-text-primary md:text-base">
                    {selectedDialCode}
                  </span>
                )}
                <input
                  id="hardware-phone"
                  name="PhoneNumber_countrycode"
                  type="tel"
                  placeholder="Phone number"
                  value={formData.phone}
                  onChange={handleChange}
                  maxLength={20}
                  className="h-full w-full bg-transparent text-sm font-medium text-text-primary outline-none placeholder:text-gray-400 md:text-base"
                  inputMode="tel"
                  autoComplete="tel"
                  aria-labelledby="hardware-phone-label"
                  required
                />
              </div>
            </div>
            {errors.phone ? <p className="mt-1 text-sm text-red-500">{errors.phone}</p> : null}
          </div>

          <Select
            id="hardware-company-size"
            name="Dropdown"
            label="Company size*"
            size="md"
            value={formData.companySize}
            onChange={handleChange}
            selectClassName={selectFieldClassName(formData.companySize)}
            error={errors.companySize}
            required
          >
            <option value="" disabled>
              Company size
            </option>
            {COMPANY_SIZE_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          <Select
            id="hardware-locations"
            name="Dropdown1"
            label="Number of locations*"
            size="md"
            value={formData.locations}
            onChange={handleChange}
            selectClassName={selectFieldClassName(formData.locations)}
            error={errors.locations}
            required
          >
            <option value="" disabled>
              Number of locations
            </option>
            {LOCATION_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          <Select
            id="hardware-screens"
            name="Dropdown2"
            label="Number of screens*"
            size="md"
            value={formData.screens}
            onChange={handleChange}
            selectClassName={selectFieldClassName(formData.screens)}
            error={errors.screens}
            required
          >
            <option value="" disabled>
              Number of screens
            </option>
            {SCREEN_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          <Select
            id="hardware-primary-need"
            name="Dropdown3"
            label="Primary need*"
            size="md"
            value={formData.primaryNeed}
            onChange={handleChange}
            selectClassName={selectFieldClassName(formData.primaryNeed)}
            error={errors.primaryNeed}
            required
          >
            <option value="" disabled>
              Primary need
            </option>
            {PRIMARY_NEED_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>

          {submitStatus === "error" ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 sm:col-span-2">
              <p className="text-sm text-red-800">
                Sorry, there was an error sending your request. Please try again.
              </p>
            </div>
          ) : null}

          <div className="pt-1 sm:col-span-2">
            <NewButton
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              disabled={isSubmitting}
              data-mixpanel-id="hardware-quote-form-submit"
            >
              {isSubmitting ? "Submitting..." : "Request a Hardware Quote"}
            </NewButton>
            <p className="mt-3 text-center text-xs text-text-secondary">
              No commitment required. A specialist will scope your rollout with you.
            </p>
          </div>
        </form>
      ) : null}
      <CalendlyWidget
        isOpen={calendlyUserData !== null}
        onClose={() => setCalendlyUserData(null)}
        calendlyUrl={HARDWARE_CALENDLY_URL}
        autoPopulate={calendlyUserData ?? undefined}
      />
    </div>
  );
}
